import React from 'react';

import MenuList from '@mui/material/MenuList';

import LockIcon from '@mui/icons-material/Lock';
import SpeedIcon from '@mui/icons-material/Speed';
import IosShareIcon from '@mui/icons-material/IosShare';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';

import { isFeatureEnabled } from 'ff/common/ffAppUtils';

import MenuListItem from 'ff/debrief/components/menu/MenuListItem';
import { useDebriefModal } from 'ff/debrief/modalManager';
import {
    useCurrentTracklog,
    useDebriefApp,
    useAnalytics,
} from 'ff/debrief/hooks';
import { buildShareUrl, hasDebriefLicense } from 'ff/debrief/utils';

interface Props {
    onMenuItemClick?: () => void;
}

const TracklogMenu: React.FC<Props> = props => {
    const {
        logInfoDialogOpened,
        logShareButtonClicked,
        logMakePrivateClicked,
    } = useAnalytics();
    const { openToast, isShareApp } = useDebriefApp();
    const {
        currentTracklog,
        shareUrl,
        isOwnTracklog,
        isPublic,
        setShareState,
    } = useCurrentTracklog();

    const { open: openOtherInfo } = useDebriefModal('OtherInfo');
    const { open: openDownloads } = useDebriefModal('Downloads');
    const { open: openDelete } = useDebriefModal('DeleteTracklog');
    const { open: openShare } = useDebriefModal('Share');
    const { open: openVspeeds } = useDebriefModal('Vspeed');
    const { open: openLogbookLink } = useDebriefModal('LinkLogbook');

    const handleOtherInfoClick = () => {
        openOtherInfo();
        props.onMenuItemClick();
        logInfoDialogOpened({
            score: currentTracklog?.analysisSummary?.score || null,
        });
    };
    const handleDownloadClick = () => {
        openDownloads();
        props.onMenuItemClick();
    };
    const handleVspeedCLick = () => {
        openVspeeds();
        props.onMenuItemClick();
    };
    const handleDeleteClick = () => {
        openDelete();
        props.onMenuItemClick();
    };
    const handleLinkLogbookClick = () => {
        openLogbookLink();
        props.onMenuItemClick();
    };
    const handleShareClick = () => {
        if (isFeatureEnabled('new_share_debrief_feature')) {
            logShareButtonClicked();
            if (isPublic) {
                navigator.clipboard
                    .writeText(shareUrl)
                    .then(() => openToast('Share link copied to clipboard'))
                    .catch(() => openShare());
            } else {
                setShareState(true)
                    .then(({ trackUuid, shareId }) =>
                        navigator.clipboard.writeText(
                            buildShareUrl(trackUuid, shareId)
                        )
                    )
                    .then(() => openToast('Share link copied to clipboard'))
                    .catch(() => openShare());
            }
        } else {
            openShare();
        }
        props.onMenuItemClick();
    };
    const handleMakePrivateClick = () => {
        logMakePrivateClicked();
        setShareState(false).then(() =>
            openToast('Debrief has been made Private')
        );
        props.onMenuItemClick();
    };

    return (
        <MenuList>
            <MenuListItem
                icon={InsertDriveFileIcon}
                onClick={handleOtherInfoClick}
            >
                Info
            </MenuListItem>
            {isOwnTracklog && isFeatureEnabled('logbook_association') && (
                <MenuListItem
                    icon={AutoStoriesIcon}
                    onClick={handleLinkLogbookClick}
                >
                    Connect Logbook
                </MenuListItem>
            )}
            {isOwnTracklog &&
                isFeatureEnabled('debrief_features') &&
                hasDebriefLicense() && (
                    <MenuListItem icon={SpeedIcon} onClick={handleVspeedCLick}>
                        Edit V-Speeds
                    </MenuListItem>
                )}
            {isOwnTracklog && !isShareApp && (
                <MenuListItem icon={IosShareIcon} onClick={handleShareClick}>
                    Share
                </MenuListItem>
            )}
            {isOwnTracklog && !isShareApp && isPublic && (
                <MenuListItem icon={LockIcon} onClick={handleMakePrivateClick}>
                    Make Private
                </MenuListItem>
            )}
            <MenuListItem icon={DownloadIcon} onClick={handleDownloadClick}>
                Download
            </MenuListItem>
            {isOwnTracklog && !isShareApp && (
                <MenuListItem icon={DeleteIcon} onClick={handleDeleteClick}>
                    Delete
                </MenuListItem>
            )}
        </MenuList>
    );
};

export default TracklogMenu;
